import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PMPagination } from "../../../utils/PMPagination";
import { Link, useNavigate } from "react-router-dom";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { 
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../contexts/settings-context";

export const HourRatesTable = (props) => {
  const { itemState, onPageChange, itemsCount, page, perPage, currentCount, onDelete } = props;
  const { hourRates: data, isLoading, errors } = itemState;
  const { t } = useTranslation();
  const { priceFormatter } = useSettings();
  const navigate = useNavigate();

  const HourRateCard = ({ item }) => {
    const isActive = item.active !== false;
    
    return (
      <div 
        className="bg-white shadow-sm rounded-lg border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
        onClick={() => navigate("/dashboard/offers/hour-rates/" + item.id)}
      >
        <div className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {item.title || t("Untitled")}
                </h3>
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  isActive 
                    ? "bg-green-100 text-green-800" 
                    : "bg-gray-100 text-gray-800"
                }`}>
                  {isActive ? t("Aktiv") : t("Inaktiv")}
                </span>
              </div>
              
              {item.description && (
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                  {item.description}
                </p>
              )}
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">
                    {t("Timepris")}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {priceFormatter.format(item.hour_rate || 0)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">
                    {t("Kostpris")}
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {priceFormatter.format(item.cost_rate || 0)}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/dashboard/offers/hour-rates/" + item.id);
                }}
                className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                title={t("Rediger")}
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onDelete) {
                    onDelete(item, e);
                  }
                }}
                className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                title={t("Slett")}
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (errors) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="text-center text-red-600">
          {t("Kunne ikke laste timesatser")}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white shadow-sm rounded-lg p-6">
        <ResourceEmpty message={t("Ingen timesatser funnet")} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        {data.map((item) => (
          <HourRateCard key={item.id} item={item} />
        ))}
      </div>
      
      {itemsCount > 0 && (
        <div className="bg-white px-6 py-4 border-t border-gray-200 rounded-b-lg">
          <PMPagination
            totalCount={itemsCount}
            currentCount={currentCount}
            page={page}
            perPage={perPage}
            onPageChange={onPageChange}
            onPerPageChange={(newPerPage) => {
              onPageChange(1);
              // Note: perPage change would need to be handled by parent
            }}
          />
        </div>
      )}
    </div>
  );
};

HourRatesTable.defaultProps = {
  itemsCount: 0,
  currentCount: 0,
  page: 1,
  perPage: 10,
  onPageChange: () => {},
  onDelete: () => {},
};

